/**
 * InterviewSession — the live interview render loop, with audio-in and
 * per-objective review & repair (specs: interview_audio_in, interview_per_objective_review_repair).
 *
 * Per-turn: respondent answers by TEXT or AUDIO (their choice each turn).
 *   Audio: record → upload+transcribe → transcript becomes the answer,
 *   submitted via the normal elicit call. NO per-turn confirm step (that
 *   was superseded — confirmation now happens once per objective, below).
 *
 * Per-objective review (the pause point): when an objective reaches
 * "complete" (awaiting_review=true), STOP — show the full exchange and two
 * paths:
 *   Path A — edit on-screen (surface errors), confirm → governing_exchange set.
 *   Path B — "Start a further discussion" (comprehension errors) → repair
 *            flow: capture an explanation (text or audio), fire a FRESH
 *            elicitation call (repair_context auto-derived server-side),
 *            run its own back-and-forth, return to this same review pause
 *            for the REPAIR exchange.
 *
 * Append-with-priority: both exchanges are kept (superseded_exchanges is
 * audit-only, shown read-only); governing_exchange is what's fed downstream.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth.jsx";
import { useSpeech } from "../../hooks/useSpeech.js";
import { useVoiceTurn } from "../../hooks/useVoiceTurn.js";
import VoicePill from "../../components/VoicePill.jsx";
import ClientBreadcrumb from "../../components/ClientBreadcrumb.jsx";

// Audio capture lives in useVoiceTurn / VoicePill now (Plan F); the upload
// of a recorded answer is `submitAudio` in the session below.

// ── Bounded directive renderer ────────────────────────────────────────────────

function DirectiveRenderer({ directive, askedQuestion, onAnswer, submitting, voice, interim, transcribing, audioError }) {
  switch (directive) {
    case "ask_question":
      return (
        <AskQuestionRenderer
          askedQuestion={askedQuestion} onAnswer={onAnswer} submitting={submitting}
          voice={voice} interim={interim} transcribing={transcribing} audioError={audioError}
        />
      );
    case "complete":
      return null;   // handled at session level (per-objective review takes over)
    default:
      return (
        <div style={d.violation}>
          <p style={d.violationTitle}>Unknown directive: "{directive}"</p>
          <p style={d.violationBody}>This directive is outside the bounded vocabulary (contract §4). Please report this to the development team.</p>
        </div>
      );
  }
}

function AskQuestionRenderer({ askedQuestion, onAnswer, submitting, voice, interim, transcribing, audioError }) {
  const [answer, setAnswer]   = useState("");
  const [dirty,  setDirty]    = useState(false);

  function handleSubmit(text) {
    const val = (text ?? answer).trim();
    if (!val) { setDirty(true); return; }
    onAnswer(val);
    setAnswer("");
    setDirty(false);
  }

  const listening = voice === "listening";
  const busy = submitting || transcribing || voice === "thinking";

  return (
    <div style={d.askBlock}>
      <p style={d.questionLabel}>Question</p>
      <p style={d.question}>{askedQuestion}</p>

      {/* Live words while the pill is listening — reassurance that they are
          being heard; the server's transcript of the recording is the answer. */}
      {listening && (
        <p style={d.interimPreview} aria-live="polite">
          {interim ? interim : <span style={d.interimListening}>Listening…</span>}
        </p>
      )}
      {transcribing && <p style={d.status}>Transcribing…</p>}
      {audioError && <p style={d.audioErrorMsg}>{audioError}</p>}

      {busy ? (
        voice === "thinking" && !transcribing
          ? <p style={d.audioSubmittedMsg}>✓ Got your answer — moving to the next question…</p>
          : null
      ) : (
        <>
          <label style={d.answerLabel}>
            {voice && voice !== "idle" && voice !== "denied" ? "Or type your answer" : "Your answer"}
            <textarea
              style={{ ...d.answerTextarea, ...(dirty && !answer.trim() ? d.answerError : {}) }}
              rows={4}
              value={answer}
              onChange={e => { setAnswer(e.target.value); setDirty(false); }}
              placeholder="Type the answer…"
              onKeyDown={e => e.key === "Enter" && e.metaKey && handleSubmit()}
            />
            {dirty && !answer.trim() && <p style={d.errorMsg}>An answer is required before continuing.</p>}
          </label>
          <div style={d.answerActions}>
            <button style={{ ...d.submitBtn, ...(submitting ? d.muted : {}) }}
              onClick={() => handleSubmit()} disabled={submitting}>
              {submitting ? "Submitting…" : "Submit answer →"}
            </button>
            <p style={d.submitHint}>⌘ + Enter</p>
          </div>
        </>
      )}
    </div>
  );
}

// ── Per-objective review pause ────────────────────────────────────────────────
// Path A (edit on-screen) / Path B (start a further discussion).

function ObjectiveReview({ interaction, onAcceptPath, onStartRepair, busy, clientId, interviewId, objectiveId }) {
  const [editedTurns, setEditedTurns] = useState(
    () => (interaction.conversation_so_far || []).map(t => ({ ...t }))
  );
  const [showRepair,  setShowRepair]  = useState(false);
  const [explanation, setExplanation] = useState("");
  const [audioError,  setAudioError]  = useState(null);

  function updateTurnAnswer(idx, value) {
    setEditedTurns(prev => prev.map((t, i) => i === idx ? { ...t, answer: value } : t));
  }

  function handleAccept() {
    onAcceptPath(editedTurns);
  }

  function handleRepairSubmit(text) {
    const val = (text ?? explanation).trim();
    if (!val) return;
    onStartRepair(val);
    setShowRepair(false);
    setExplanation("");
  }

  const hasRepairHistory = (interaction.superseded_exchanges || []).length > 0;

  return (
    <div style={rv.card}>
      <div style={rv.header}>
        <p style={rv.label}>Review this exchange</p>
        {hasRepairHistory && (
          <span style={rv.repairBadge}>
            {interaction.superseded_exchanges.length} prior attempt{interaction.superseded_exchanges.length !== 1 ? "s" : ""} on record
          </span>
        )}
      </div>

      {/* The exchange — editable in place (Path A surface) */}
      <div style={rv.transcript}>
        {editedTurns.map((turn, i) => (
          <div key={i} style={rv.turn}>
            <p style={rv.turnQ}>{turn.asked_question}</p>
            <textarea
              style={rv.turnAnswerInput}
              value={turn.answer}
              onChange={e => updateTurnAnswer(i, e.target.value)}
              rows={2}
            />
          </div>
        ))}
      </div>

      {interaction.final_text && (
        <div style={rv.finalBlock}>
          <p style={rv.finalLabel}>Distilled summary (what feeds the profile)</p>
          <p style={rv.finalText}>{interaction.final_text}</p>
        </div>
      )}

      {/* Audit — superseded exchanges, read-only */}
      {hasRepairHistory && (
        <details style={rv.auditDetails}>
          <summary style={rv.auditSummary}>View prior attempt{interaction.superseded_exchanges.length !== 1 ? "s" : ""} (audit trail)</summary>
          {interaction.superseded_exchanges.map((sx, i) => (
            <div key={i} style={rv.auditBlock}>
              <p style={rv.auditExplain}>Why it was redone: "{sx.explanation_for_repair}"</p>
              {sx.turns.map((t, j) => (
                <div key={j} style={rv.auditTurn}>
                  <p style={rv.turnQ}>{t.asked_question}</p>
                  <p style={rv.auditAnswer}>{t.answer}</p>
                </div>
              ))}
            </div>
          ))}
        </details>
      )}

      {/* Path B — start a further discussion */}
      {!showRepair ? (
        <div style={rv.actions}>
          <button style={rv.repairBtn} onClick={() => setShowRepair(true)} disabled={busy}>
            Continue commenting on this topic
          </button>
          <button style={{ ...rv.acceptBtn, ...(busy ? rv.muted : {}) }} onClick={handleAccept} disabled={busy}>
            {busy ? "Saving…" : "Confirm and continue →"}
          </button>
        </div>
      ) : (
        <div style={rv.repairBlock}>
          <p style={rv.repairPrompt}>What did this exchange miss? Explain what you actually meant — the AI will ask follow-ups informed by your correction.</p>
          <AudioRecorder
            onTranscribed={t => handleRepairSubmit(t)}
            onError={setAudioError}
            disabled={busy}
            clientId={clientId} interviewId={interviewId} objectiveId={objectiveId}
          />
          {audioError && <p style={d.audioErrorMsg}>{audioError}</p>}
          <textarea
            style={d.answerTextarea}
            rows={3}
            value={explanation}
            onChange={e => setExplanation(e.target.value)}
            placeholder="e.g. 'It's not about triggers — it's that she loses track mid-shower when counting.'"
          />
          <div style={rv.repairActions}>
            <button style={{ ...rv.acceptBtn, ...(busy ? rv.muted : {}) }} onClick={() => handleRepairSubmit()} disabled={busy}>
              {busy ? "Starting…" : "Start discussion →"}
            </button>
            <button style={rv.cancelRepairBtn} onClick={() => setShowRepair(false)} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Progress indicator ────────────────────────────────────────────────────────

function ObjectivePill({ obj, current, done, awaitingReview }) {
  let bg = "var(--color-bg-inset)", color = "var(--color-text-tertiary)", label = obj.order_index + 1;
  if (done)             { bg = "var(--color-accent-subtle)"; color = "var(--color-accent)"; label = "✓"; }
  if (awaitingReview)    { bg = "rgba(245,158,11,0.15)"; color = "#d97706"; label = "!"; }
  if (current)           { bg = "var(--color-accent)"; color = "#fff"; }
  return <div style={{ ...p.pill, background: bg, color }}>{label}</div>;
}

// Two tiny effect-only helpers so the voice loop does not need a button
// between objectives: move to the next one, or close the mic at the end.
function VoiceAdvance({ onAdvance }) {
  useEffect(() => { const t = setTimeout(onAdvance, 600); return () => clearTimeout(t); }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}
function VoiceFinish({ onFinish }) {
  useEffect(() => { onFinish(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// ── Main ──────────────────────────────────────────────────────────────────────

// What the pill says while it waits (Oct 29): the wait is visible, and says
// what it's waiting for. Wording is a first draft for David.
const VOICE_WORDS = {
  joining:  "Getting ready…",
  reading:  "Reading the question…",
  thinking: "Getting the next question…",
};

export default function InterviewSession({ clientId: clientIdProp, interviewId: interviewIdProp } = {}) {
  // An interview link (/i/:token, Oct 29) passes the ids in; the usual
  // routes carry them in the URL.
  const params = useParams();
  const clientId    = clientIdProp || params.clientId;
  const interviewId = interviewIdProp || params.interviewId;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [interview,    setInterview]    = useState(null);
  const [clientName,   setClientName]   = useState("");
  const [loading,      setLoading]      = useState(true);
  const [currentObjIdx, setCurrentObjIdx] = useState(0);
  const [submitting,   setSubmitting]   = useState(false);
  const [elicitError,  setElicitError]  = useState(null);
  const [feeding,      setFeeding]      = useState(false);
  const [feedDone,     setFeedDone]     = useState(false);

  // Voice (Plan F): the pill reads each question, listens, and submits the
  // recording after two seconds of quiet. TTS is the same hook as before.
  const { speak, prepare: prepareSpeech, cancel: cancelSpeech, speaking, unlock } = useSpeech();
  const [transcribing, setTranscribing] = useState(false);
  const [audioError, setAudioError]     = useState(null);
  const [voiceOffered, setVoiceOffered] = useState(false);   // coordinator opted in
  const currentObjIdRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // The name comes with the interview: someone here only to answer it
      // can't open the profile itself (Oct 28).
      const iv = await fetch(`/api/clients/${clientId}/interviews/${interviewId}`).then(r => r.json());
      setInterview(iv);
      setClientName(iv.client_name || "");

      const firstUnresolved = iv.objectives?.findIndex(
        o => !o.interaction || o.interaction.governing_exchange === null || o.interaction.governing_exchange === undefined
      );
      setCurrentObjIdx(firstUnresolved >= 0 ? firstUnresolved : (iv.objectives?.length || 0) - 1);
    } catch (e) { /* handled by interview === null */ }
    finally { setLoading(false); }
  }, [clientId, interviewId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!interview) return;
    const obj = interview.objectives?.[currentObjIdx];
    if (!obj) return;
    const ix = obj.interaction;
    // Fire the first elicit call only if truly nothing has started yet
    if (!ix || (ix.status === "pending" && !ix.pending_question)) {
      fireElicit(obj.id, null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentObjIdx, interview?.id]);

  async function fireElicit(objectiveId, answer) {
    setSubmitting(true);
    setElicitError(null);
    try {
      const res  = await fetch(
        `/api/clients/${clientId}/interviews/${interviewId}/objectives/${objectiveId}/elicit`,
        { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ answer }) }
      );
      const data = await res.json();
      if (!res.ok) {
        setElicitError(data.respondent_fallback || data.error || "An error occurred.");
        return;
      }
      await load();
    } catch (e) {
      setElicitError("Network error — please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function wrapUp(objectiveId) {
    setSubmitting(true);
    setElicitError(null);
    try {
      const res = await fetch(
        `/api/clients/${clientId}/interviews/${interviewId}/objectives/${objectiveId}/wrap-up`,
        { method: "POST", headers: { "Content-Type": "application/json" } }
      );
      const data = await res.json();
      if (!res.ok) {
        setElicitError(data.error || "Couldn't wrap up this objective.");
        return;
      }
      await load();   // objective now shows the review pause
    } catch (e) {
      setElicitError("Network error — please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleReviewAccept(objectiveId, editedTurns) {
    setSubmitting(true);
    setElicitError(null);
    try {
      const res  = await fetch(
        `/api/clients/${clientId}/interviews/${interviewId}/objectives/${objectiveId}/review`,
        { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ edited_turns: editedTurns }) }
      );
      const data = await res.json();
      if (!res.ok) { setElicitError(data.error || "Could not save review."); return; }
      await load();
      // Auto-advance to the next unresolved objective
      const nextIdx = interview.objectives?.findIndex(
        (o, i) => i > currentObjIdx && (!o.interaction || !o.interaction.governing_exchange)
      );
      if (nextIdx >= 0) setCurrentObjIdx(nextIdx);
    } catch (e) {
      setElicitError("Network error — please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleStartRepair(objectiveId, missedExplanation) {
    setSubmitting(true);
    setElicitError(null);
    try {
      const res  = await fetch(
        `/api/clients/${clientId}/interviews/${interviewId}/objectives/${objectiveId}/repair`,
        { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ missed_explanation: missedExplanation }) }
      );
      const data = await res.json();
      if (!res.ok) { setElicitError(data.error || "Could not continue commenting on this topic."); return; }
      await load();
    } catch (e) {
      setElicitError("Network error — please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleFeed() {
    setFeeding(true);
    try {
      const res  = await fetch(`/api/clients/${clientId}/interviews/${interviewId}/feed`, { method: "POST" });
      const data = await res.json();
      if (!res.ok) { setElicitError(data.error || "Feed failed"); return; }
      setFeedDone(true);
    } catch (e) { setElicitError("Feed failed: " + e.message); }
    finally { setFeeding(false); }
  }

  // The pending question, computed before any early return so hook order is
  // stable; keyed on its text so the pill reads each new one once.
  const _objs = interview?.objectives || [];
  const _curObj = _objs[currentObjIdx];
  const _curIx = _curObj?.interaction;
  const _pendingQuestion = _curIx && !_curIx.awaiting_review ? _curIx.pending_question : null;
  const respondentModeEarly = !!interview?.viewer_is_respondent && !interview?.viewer_may_edit;
  currentObjIdRef.current = _curObj?.id || null;

  // A recorded answer: upload, transcribe, and submit the transcript exactly
  // as a typed answer would be. Called by the voice hook when a turn ends.
  async function submitAudio(blob) {
    const objectiveId = currentObjIdRef.current;
    if (!objectiveId) return;
    setTranscribing(true); setAudioError(null);
    try {
      const form = new FormData();
      form.append("file", blob, "answer.webm");
      const res  = await fetch(
        `/api/clients/${clientId}/interviews/${interviewId}/objectives/${objectiveId}/audio`,
        { method: "POST", body: form }
      );
      const data = await res.json();
      if (!res.ok) { setAudioError(data.error || "Transcription failed — please type your answer instead."); return; }
      const text = (data.transcript || "").trim();
      if (!text) { setAudioError("I didn't catch that — say it again, or type it."); return; }
      await fireElicit(objectiveId, text);
    } catch (e) {
      setAudioError("Could not reach the server — please type your answer instead.");
    } finally {
      setTranscribing(false);
    }
  }

  // Start making each question's audio the moment its text arrives, before
  // anyone taps, so it plays without a pause (Oct 29). Only where voice is on
  // offer: a respondent on their own, or a coordinator who turned it on.
  useEffect(() => {
    if (_pendingQuestion && (respondentModeEarly || voiceOffered)) prepareSpeech(_pendingQuestion);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_pendingQuestion, voiceOffered, respondentModeEarly]);

  const voice = useVoiceTurn({
    question: _pendingQuestion,
    speak, cancelSpeech, speaking, unlock,
    onAnswer: submitAudio,
    onEnd: () => navigate(interview?.viewer_may_edit ? `/coordinator/clients/${clientId}/edit`
      : interview?.viewer_may_view_profile ? `/clients/${clientId}` : "/"),
  });

  // A failed transcription hands the turn back: from "thinking" to "speak".
  // The hook cannot know; it sees a new question only. Re-read the same one.
  useEffect(() => {
    if (audioError && voice.state === "thinking") voice.retry?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioError]);

  if (loading) return <div style={s.page}><div style={s.content}><p style={s.hint}>Loading…</p></div></div>;
  if (!interview) return <div style={s.page}><div style={s.content}><p style={s.hint}>Interview not found.</p></div></div>;

  const objectives  = interview.objectives || [];
  const currentObj  = objectives[currentObjIdx];
  const currentIx   = currentObj?.interaction;
  const respondent  = interview.respondent;

  // Plan D: the person answering on their own, later. The coordinator's
  // review-and-repair pause is not theirs; when an objective completes they
  // move on, and when all are answered they are thanked and done. Review
  // happens afterwards, from the hub, by the coordinator.
  const respondentMode = !!interview.viewer_is_respondent && !interview.viewer_may_edit;
  const answered = o => !!(o.interaction?.governing_exchange || o.interaction?.status === "complete");
  const allDone     = objectives.length > 0 && (respondentMode
                        ? objectives.every(answered)
                        : objectives.every(o => o.interaction?.governing_exchange));
  const awaitingReview = !respondentMode && currentIx?.awaiting_review;
  const objectiveDone  = respondentMode && currentIx?.status === "complete";
  const firstName = (clientName || "").split(" ")[0];
  const voiceOn   = voice.state !== "idle" && voice.state !== "denied" && voice.state !== "done";



  return (
    <div style={s.page}>
      <div style={s.content}>
        {interview.viewer_may_view_profile !== false && (
          <ClientBreadcrumb clientName={clientName} role={user?.role} clientId={clientId} />
        )}

        <div style={s.header}>
          {respondentMode ? (
            <div>
              <h1 style={s.title}>A few questions about {firstName || "them"}</h1>
              {interview.respondent_note && <p style={s.note}>“{interview.respondent_note}”</p>}
              <p style={s.respondentLine}>About ten minutes. Answer in your own words — there are no wrong answers. You can stop and come back.</p>
            </div>
          ) : (
            <div>
              <h1 style={s.title}>Interview</h1>
              <p style={s.respondentLine}>
                {respondent?.display_name || respondent?.role || "Respondent"}
                <span style={s.roleChip}>{respondent?.role}</span>
              </p>
            </div>
          )}
          <div style={s.pills}>
            {objectives.map((obj, i) => (
              <button key={obj.id} style={s.pillBtn} onClick={() => setCurrentObjIdx(i)}>
                <ObjectivePill
                  obj={obj} current={i === currentObjIdx}
                  done={respondentMode ? answered(obj) : !!obj.interaction?.governing_exchange}
                  awaitingReview={obj.interaction?.awaiting_review}
                />
              </button>
            ))}
          </div>
        </div>

        {elicitError && (
          <div style={s.errorBox}>
            <p style={s.errorText}>{elicitError}</p>
            <button style={s.retryBtn} onClick={() => { setElicitError(null); }}>Dismiss</button>
          </div>
        )}

        {/* Voice (Plan F). A respondent on their own gets the pill up front; a
            coordinator in the room starts in text and can turn it on. The label
            says what the control does — hear the questions, answer out loud —
            not who you're talking to: it's not Lucas, and the objectives may
            not even be his (David, Oct 15). */}
        {!allDone && currentObj && (respondentMode || voiceOffered) && (
          <VoicePill state={voice.state} level={voice.level}
                     startLabel="Listen then answer by voice"
                     words={VOICE_WORDS}
                     onStart={voice.start} onTap={voice.tapPill} onEnd={() => {
                       if (window.confirm("Stop here? What you've answered is saved.")) voice.end();
                     }}
                     devices={voice.devices} deviceId={voice.deviceId} onPickDevice={voice.setDevice} />
        )}
        {!allDone && currentObj && !respondentMode && !voiceOffered && (
          <button type="button" style={s.useVoice} onClick={() => setVoiceOffered(true)}>Use voice</button>
        )}

        {!allDone && currentObj && (
          <div style={s.interactionCard}>
            <div style={s.objMeta}>
              <span style={s.objIndex}>Objective {currentObjIdx + 1} of {objectives.length}</span>
              {!awaitingReview && (currentIx?.conversation_so_far || []).length > 0 && (
                <span style={s.depthSignal}>
                  {(currentIx.conversation_so_far || []).length === 1
                    ? "1 question so far"
                    : `${(currentIx.conversation_so_far || []).length} questions so far`}
                  {!respondentMode && <span style={s.depthHint}> — the AI keeps going until it has what it needs</span>}
                </span>
              )}
              {currentObj.background && <p style={s.background}>{currentObj.background}</p>}
            </div>

            {/* Live conversation (only shown pre-review, since review shows its own editable copy) */}
            {!awaitingReview && (currentIx?.conversation_so_far || []).length > 0 && (
              <div style={s.transcript}>
                {currentIx.conversation_so_far.map((turn, i) => (
                  <div key={i} style={s.turn}>
                    <p style={s.turnQ}>{turn.asked_question}</p>
                    <p style={s.turnA}>{turn.answer}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Bounded directive renderer — only while still in-progress */}
            {!awaitingReview && currentIx?.pending_question && (
              <DirectiveRenderer
                directive="ask_question"
                askedQuestion={currentIx.pending_question}
                onAnswer={ans => fireElicit(currentObj.id, ans)}
                submitting={submitting}
                voice={voice.state} interim={voice.interim}
                transcribing={transcribing} audioError={audioError}
              />
            )}

            {/* Wrap-up: let the coordinator conclude this objective now, using the
                answers gathered so far, instead of continuing the back-and-forth.
                Shown once there's at least one answered turn. */}
            {!awaitingReview && currentIx?.pending_question
              && (currentIx?.conversation_so_far || []).length > 0 && (
              <button style={s.wrapUpBtn} onClick={() => wrapUp(currentObj.id)} disabled={submitting}>
                {respondentMode ? "That's all I have on this →" : "Wrap up this objective now →"}
              </button>
            )}
            {objectiveDone && voiceOn && currentObjIdx < objectives.length - 1 && (
              <VoiceAdvance onAdvance={() => setCurrentObjIdx(i => i + 1)} />
            )}
            {objectiveDone && (
              <div style={s.nextCard}>
                <p style={s.hint}>Saved. Thank you.</p>
                {currentObjIdx < objectives.length - 1 ? (
                  <button style={s.wrapUpBtn} onClick={() => setCurrentObjIdx(i => i + 1)}>Next question →</button>
                ) : null}
              </div>
            )}
            {!awaitingReview && !objectiveDone && !currentIx?.pending_question && !submitting && !elicitError && (
              <p style={s.hint}>Loading question…</p>
            )}

            {/* Per-objective review pause */}
            {awaitingReview && (
              <ObjectiveReview
                interaction={currentIx}
                busy={submitting}
                onAcceptPath={editedTurns => handleReviewAccept(currentObj.id, editedTurns)}
                onStartRepair={explanation => handleStartRepair(currentObj.id, explanation)}
                clientId={clientId} interviewId={interviewId} objectiveId={currentObj.id}
              />
            )}
          </div>
        )}

        {allDone && voiceOn && <VoiceFinish onFinish={voice.finish} />}
        {allDone && respondentMode && (
          <div style={s.doneCard}>
            <p style={s.doneTitle}>Thank you.</p>
            <p style={s.doneBody}>That's everything. {interview.coordinator_name || "The coordinator"} will see your answers and use them to help the people who support {firstName || "them"}.</p>
            {interview.viewer_may_view_profile !== false && (
              <button style={s.hubBtnPrimary} onClick={() => navigate(`/clients/${clientId}`)}>
                Go to {firstName || "the profile"}'s profile →
              </button>
            )}
          </div>
        )}

        {allDone && !respondentMode && (
          <div style={s.doneCard}>
            {feedDone ? (
              <>
                <p style={s.doneTitle}>✓ Interview content sent for extraction</p>
                <p style={s.doneBody}>The interview responses are being extracted and will appear in the coordinator review gate. Review them there before they go live.</p>
                {/* Post-feed: nothing else to do here — this is the clear single CTA */}
                <button style={s.hubBtnPrimary} onClick={() => navigate(`/coordinator/clients/${clientId}/edit`)}>
                  ← Back to editor hub
                </button>
              </>
            ) : (
              <>
                <p style={s.doneTitle}>All objectives reviewed</p>
                <p style={s.doneBody}>
                  Feed the final responses into the extraction pipeline as a supplementary source.
                  They'll flow through the existing coordinator review gate — <strong>nothing goes live without your review.</strong>
                </p>
                {elicitError && <p style={{ fontSize:"var(--text-sm)", color:"var(--color-critical)" }}>{elicitError}</p>}
                {/* Secondary (back) + primary (feed) — same convention as PlaythroughScriptEditor's action bar */}
                <div style={s.doneActions}>
                  <button style={s.hubBtnSecondary} onClick={() => navigate(`/coordinator/clients/${clientId}/edit`)}>
                    ← Back to editor hub
                  </button>
                  <button style={{ ...s.feedBtn, ...(feeding ? s.muted : {}) }} onClick={handleFeed} disabled={feeding}>
                    {feeding ? "Sending…" : "Feed to extraction →"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────


const s = {
  note:     { fontFamily: "var(--font-serif)", fontSize: "var(--text-base)", color: "var(--color-text-primary)", margin: "4px 0 6px", lineHeight: 1.45 },
  nextCard: { display: "flex", flexDirection: "column", gap: "var(--space-3)", alignItems: "flex-start" },
  page:          { minHeight: "100dvh", background: "var(--color-bg-subtle)" },
  content:       { maxWidth: 720, margin: "0 auto", padding: "var(--space-6)", display: "flex", flexDirection: "column", gap: "var(--space-5)" },
  header:        { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "var(--space-4)", flexWrap: "wrap" },
  title:         { fontSize: "var(--text-2xl)", fontWeight: "var(--weight-bold)", color: "var(--color-text-primary)" },
  respondentLine:{ fontSize: "var(--text-base)", color: "var(--color-text-secondary)", display: "flex", alignItems: "center", gap: "var(--space-2)", marginTop: "var(--space-1)" },
  roleChip:      { fontSize: 10, background: "var(--color-bg-inset)", color: "var(--color-text-tertiary)", borderRadius: "var(--radius-full)", padding: "2px var(--space-2)", fontWeight: "var(--weight-semibold)", textTransform: "uppercase", letterSpacing: "0.05em" },
  useVoice:     { font: "inherit", fontSize: "var(--text-sm)", color: "var(--color-accent)", background: "none", border: "1px solid var(--color-accent)", borderRadius: 999, padding: "6px 14px", cursor: "pointer", alignSelf: "center" },
  autoReadToggle:{ display: "inline-flex", alignItems: "center", gap: "var(--space-2)", fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", cursor: "pointer", whiteSpace: "nowrap" },
  pills:         { display: "flex", gap: "var(--space-2)", flexWrap: "wrap" },
  pillBtn:       { background: "none", border: "none", padding: 0, cursor: "pointer" },
  hint:          { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" },
  errorBox:      { background: "var(--color-critical-bg)", border: "1px solid var(--color-critical)", borderRadius: "var(--radius-md)", padding: "var(--space-3) var(--space-4)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-3)" },
  errorText:     { fontSize: "var(--text-sm)", color: "var(--color-critical)", margin: 0 },
  retryBtn:      { fontSize: "var(--text-xs)", color: "var(--color-critical)", background: "none", border: "1px solid var(--color-critical)", borderRadius: "var(--radius-sm)", padding: "2px var(--space-2)", cursor: "pointer", whiteSpace: "nowrap" },
  interactionCard: { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: "var(--space-5)", display: "flex", flexDirection: "column", gap: "var(--space-4)" },
  objMeta:       { display: "flex", flexDirection: "column", gap: "var(--space-1)", paddingBottom: "var(--space-3)", borderBottom: "1px solid var(--color-border)" },
  objIndex:      { fontSize: "var(--text-xs)", fontWeight: "var(--weight-semibold)", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-tertiary)" },
  depthSignal:   { fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", display: "block", marginTop: 2 },
  depthHint:     { color: "var(--color-text-tertiary)" },
  wrapUpBtn:     { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer", alignSelf: "flex-start", marginTop: "var(--space-2)" },
  background:    { fontSize: "var(--text-sm)", color: "var(--color-text-primary)", lineHeight: "var(--leading-normal)" },
  transcript:    { display: "flex", flexDirection: "column", gap: "var(--space-3)", padding: "var(--space-3)", background: "var(--color-bg-subtle)", borderRadius: "var(--radius-md)" },
  turn:          { display: "flex", flexDirection: "column", gap: "var(--space-1)" },
  turnQ:         { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", fontStyle: "italic" },
  turnA:         { fontSize: "var(--text-sm)", color: "var(--color-text-primary)" },
  doneCard:      { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: "var(--space-6)", display: "flex", flexDirection: "column", gap: "var(--space-4)" },
  doneTitle:     { fontSize: "var(--text-lg)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)" },
  doneBody:      { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", lineHeight: "var(--leading-normal)" },
  doneActions:   { display: "flex", gap: "var(--space-3)", alignItems: "center" },
  feedBtn:        { fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", background: "var(--color-accent)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  hubBtnPrimary:  { fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", background: "var(--color-accent)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer", alignSelf: "flex-start" },
  hubBtnSecondary:{ fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "var(--color-text-secondary)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  muted:         { opacity: 0.5, cursor: "not-allowed" },
};

const d = {
  askBlock:      { display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  questionLabel: { fontSize: 10, fontWeight: "var(--weight-semibold)", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-tertiary)", margin: 0 },
  status:        { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", fontStyle: "italic" },
  interimPreview:{ fontSize: "var(--text-sm)", color: "var(--color-text-primary)", background: "var(--color-bg-subtle)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-3)", margin: 0, minHeight: "1.4em", lineHeight: "var(--leading-normal)" },
  interimListening: { color: "var(--color-text-tertiary)", fontStyle: "italic" },
  question:      { fontSize: "var(--text-base)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)", lineHeight: "var(--leading-normal)" },
  answerLabel:   { display: "flex", flexDirection: "column", gap: "var(--space-1)", fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" },
  answerTextarea:{ fontSize: "var(--text-base)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-3)", background: "var(--color-bg-base)", color: "var(--color-text-primary)", fontFamily: "inherit", resize: "vertical", lineHeight: "var(--leading-normal)" },
  answerError:   { borderColor: "var(--color-critical)" },
  errorMsg:      { fontSize: "var(--text-xs)", color: "var(--color-critical)" },
  audioErrorMsg: { fontSize: "var(--text-xs)", color: "#d97706" },
  audioSubmittedMsg: { fontSize: "var(--text-sm)", color: "var(--color-accent)", fontWeight: "var(--weight-medium)" },
  answerActions: { display: "flex", alignItems: "center", gap: "var(--space-3)" },
  submitBtn:     { fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", background: "var(--color-accent)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  submitHint:    { fontSize: 11, color: "var(--color-text-tertiary)" },
  muted:         { opacity: 0.5, cursor: "not-allowed" },
  violation:     { background: "var(--color-critical-bg)", border: "1px solid var(--color-critical)", borderRadius: "var(--radius-md)", padding: "var(--space-4)", display: "flex", flexDirection: "column", gap: "var(--space-1)" },
  violationTitle:{ fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "var(--color-critical)" },
  violationBody: { fontSize: "var(--text-sm)", color: "var(--color-critical)" },
};

const p = {
  pill: { width: 28, height: 28, borderRadius: "var(--radius-full)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: "var(--weight-semibold)" },
};

const rv = {
  card:        { background: "var(--color-bg-subtle)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-4)", display: "flex", flexDirection: "column", gap: "var(--space-4)" },
  header:      { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-3)" },
  label:       { fontSize: "var(--text-xs)", fontWeight: "var(--weight-semibold)", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-tertiary)", margin: 0 },
  repairBadge: { fontSize: 11, color: "#d97706", background: "rgba(245,158,11,0.12)", borderRadius: "var(--radius-full)", padding: "2px var(--space-2)" },
  transcript:  { display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  turn:        { display: "flex", flexDirection: "column", gap: "var(--space-1)" },
  turnQ:       { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", fontStyle: "italic" },
  turnAnswerInput: { fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2)", background: "var(--color-bg-base)", color: "var(--color-text-primary)", fontFamily: "inherit", resize: "vertical" },
  finalBlock:  { background: "var(--color-accent-subtle)", borderRadius: "var(--radius-md)", padding: "var(--space-3)" },
  finalLabel:  { fontSize: 10, fontWeight: "var(--weight-semibold)", color: "var(--color-accent)", textTransform: "uppercase", letterSpacing: "0.06em", margin: 0 },
  finalText:   { fontSize: "var(--text-sm)", color: "var(--color-text-primary)", marginTop: "var(--space-1)" },
  auditDetails:{ fontSize: "var(--text-sm)" },
  auditSummary:{ fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", cursor: "pointer" },
  auditBlock:  { marginTop: "var(--space-2)", padding: "var(--space-3)", background: "var(--color-bg-inset)", borderRadius: "var(--radius-sm)", display: "flex", flexDirection: "column", gap: "var(--space-2)" },
  auditExplain:{ fontSize: "var(--text-xs)", color: "#d97706", fontStyle: "italic" },
  auditTurn:   { display: "flex", flexDirection: "column", gap: 2 },
  auditAnswer: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
  actions:     { display: "flex", justifyContent: "space-between", gap: "var(--space-3)", flexWrap: "wrap" },
  repairBtn:   { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer" },
  acceptBtn:   { fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", background: "var(--color-accent)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  repairBlock: { display: "flex", flexDirection: "column", gap: "var(--space-3)", padding: "var(--space-3)", background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.25)", borderRadius: "var(--radius-md)" },
  repairPrompt:{ fontSize: "var(--text-sm)", color: "#92400e" },
  repairActions: { display: "flex", gap: "var(--space-3)" },
  cancelRepairBtn: { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", background: "none", border: "none", cursor: "pointer" },
  muted:       { opacity: 0.5, cursor: "not-allowed" },
};
